import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { MAHAVIDYAS, getMahavidya, mvImageFor } from "@/lib/mahavidyas";
import { useSettings } from "@/context/SettingsContext";
import { Divider } from "@/components/MotifBackground";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

const Section = ({ deva, en, children, testid }) => (
  <section className="mt-14 fade-up" data-testid={testid}>
    <div className="mb-5 flex items-baseline gap-3">
      <span className="h-px w-8 bg-[var(--gold)]" />
      <h2 className="font-deva text-2xl text-saffron deva-lh">{deva}</h2>
      <span className="overline-label text-ink-3">{en}</span>
    </div>
    {children}
  </section>
);

export default function MahavidyaDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pick, isDeva } = useLang();
  const settings = useSettings();
  const m = getMahavidya(slug);
  const idx = MAHAVIDYAS.findIndex((x) => x.slug === slug);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  if (!m) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="mahavidya-notfound">
        <p className="text-ink-2">{pick("यह महाविद्या नहीं मिली।", "This Mahavidya was not found.")}</p>
        <Link to="/mahavidya" className="mt-4 inline-block text-saffron">{pick("दस महाविद्या", "Back to the Mahavidyas")}</Link>
      </div>
    );
  }

  const prev = idx > 0 ? MAHAVIDYAS[idx - 1] : null;
  const next = idx < MAHAVIDYAS.length - 1 ? MAHAVIDYAS[idx + 1] : null;

  return (
    <div data-testid="mahavidya-page" className="pb-24">
      <Seo path={`/mahavidya/${m.slug}`} type="article"
        image={mvImageFor(m, settings)}
        title={`${m.en} (${m.deva}) — Dasha Mahavidya · Mantra, Significance & Story`}
        description={`${m.en}, Mahavidya ${m.n} of the ten wisdom goddesses. ${m.sig_en} Iconography, significance, story and bija mantra of ${m.en}.`}
        keywords={`${m.en}, ${m.deva}, Dasha Mahavidya, ${m.en} mantra, ${m.en} bija, ${m.en} significance, wisdom goddess`} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line" style={{ background: `linear-gradient(135deg, ${m.accent}18, var(--surface) 55%)` }}>
        <div className="mx-auto grid grid-cols-1 max-w-4xl items-center gap-8 px-5 py-14 md:grid-cols-[minmax(0,1fr)_auto] md:gap-12 md:px-8 md:py-20">
          <div>
            <Link to="/mahavidya" data-testid="mahavidya-back" className="inline-flex items-center gap-1.5 text-sm text-ink-3 transition-colors hover:text-saffron">
              <ArrowLeft className="h-4 w-4" /> {pick("दस महाविद्या", "The Ten Mahavidyas")}
            </Link>
            <p className="mt-6 overline-label" style={{ color: m.accent }}>{pick("महाविद्या", "Mahavidya")} {m.n} · {m.group === "ugra" ? pick("उग्र", "Ugra") : pick("सौम्य", "Saumya")}</p>
            <h1 className="mt-3 font-deva text-5xl text-ink deva-lh md:text-6xl">{m.deva}</h1>
            <p className="mt-2 font-serif-latin text-2xl text-ink-2 md:text-3xl">{m.en} <span className="text-ink-3">· {m.latin}</span></p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink-2">{pick(m.sig_hi, m.sig_en)}</p>
            <div className="mt-6"><ShareButton title={`${m.en} · Dasha Mahavidya · Dharmartha`} label={pick("साझा करें", "Share")} testid="mahavidya-share" /></div>
          </div>
          <div className="relative mx-auto w-full max-w-[15rem] md:max-w-[16rem]" data-testid="mahavidya-portrait">
            <div className="pointer-events-none absolute -inset-2 border" style={{ borderColor: m.accent + "55" }} aria-hidden="true" />
            <div className="relative overflow-hidden border bg-[var(--parchment)] shadow-[0_16px_44px_rgba(44,38,33,0.18)]" style={{ borderColor: m.accent + "88" }}>
              <img src={mvImageFor(m, settings)} alt={`${m.en} · ${m.deva}`} className="block h-auto w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-5 md:px-8">
        <Section deva="स्वरूप एवं प्रतीक" en="Iconography" testid="mahavidya-icon">
          <p className="max-w-[65ch] text-[17px] leading-relaxed text-ink-2">{pick(m.icon_hi, m.icon_en)}</p>
        </Section>

        <Section deva="महिमा एवं कथा" en="Significance & Story" testid="mahavidya-story">
          <p className="max-w-[65ch] font-serif-latin text-[17px] leading-[1.95] text-ink-2 md:text-lg">{pick(m.story_hi, m.story_en)}</p>
        </Section>

        <Section deva="बीज मंत्र" en="Bija Mantra" testid="mahavidya-mantra">
          <div className="border-l-2 border-saffron bg-[var(--surface-alt)]/50 px-6 py-5">
            <p className="break-words font-deva text-2xl leading-[1.9] text-ink">{m.bija}</p>
            <p className="mt-1.5 break-words font-serif-latin italic text-ink-2">{m.bija_iast}</p>
          </div>
          <p className="mt-3 flex items-start gap-2 text-[13px] italic text-ink-3">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
            {pick("पूर्ण साधना-मंत्र सम्प्रदाय-भेद से भिन्न होते हैं; परम्परागत रूप से गुरु से दीक्षा अपेक्षित है।", "The full sadhana mantras vary by lineage; diksha (initiation) from a guru is traditionally required.")}
          </p>
        </Section>

        <Divider className="my-16" />

        <div className="grid gap-4 sm:grid-cols-2" data-testid="mahavidya-nav">
          {prev ? (
            <button onClick={() => navigate(`/mahavidya/${prev.slug}`)} data-testid="mahavidya-prev" className="group flex items-center gap-3 border border-line bg-surface p-5 text-left transition-colors hover:border-[var(--gold)]">
              <ArrowLeft className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:-translate-x-0.5 group-hover:text-saffron" />
              <div><p className="overline-label text-ink-3">{pick("पूर्व", "Previous")} · {prev.n}</p><p className={`mt-0.5 text-lg text-ink ${isDeva ? "font-deva deva-lh" : "font-serif-latin"}`}>{pick(prev.deva, prev.en)}</p></div>
            </button>
          ) : <span />}
          {next && (
            <button onClick={() => navigate(`/mahavidya/${next.slug}`)} data-testid="mahavidya-next" className="group flex items-center justify-end gap-3 border border-line bg-surface p-5 text-right transition-colors hover:border-[var(--gold)]">
              <div><p className="overline-label text-ink-3">{pick("अगला", "Next")} · {next.n}</p><p className={`mt-0.5 text-lg text-ink ${isDeva ? "font-deva deva-lh" : "font-serif-latin"}`}>{pick(next.deva, next.en)}</p></div>
              <ArrowRight className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-saffron" />
            </button>
          )}
        </div>

        <Section deva="दस महाविद्या" en="All ten" testid="mahavidya-all">
          <div className="flex flex-wrap gap-2">
            {MAHAVIDYAS.map((x) => (
              <Link key={x.slug} to={`/mahavidya/${x.slug}`} className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-sm transition-colors ${x.slug === slug ? "border-[var(--gold)] bg-[rgba(184,134,11,0.08)] text-ink" : "border-line text-ink-2 hover:border-[var(--gold)]"}`}>
                <span className="font-serif-latin text-xs text-gold">{x.n}</span>
                <span className={isDeva ? "font-deva deva-lh" : ""}>{pick(x.deva, x.en)}</span>
              </Link>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
